'use client';

import { useState, useMemo } from 'react';
import {
  Search,
  Plus,
  Eye,
  Edit2,
  Clock,
  User,
  Calendar,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { timesheets } from '../data/mock-data';
import { timesheetStatusConfig, type Timesheet } from '../types';

// 타임시트 작성 모달
function TimesheetAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [entries, setEntries] = useState([{ date: '', projectId: '', taskId: '', hours: 0, description: '' }]);

  const addEntry = () => setEntries([...entries, { date: '', projectId: '', taskId: '', hours: 0, description: '' }]);
  const removeEntry = (index: number) => {
    if (entries.length > 1) setEntries(entries.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="타임시트 작성"
      description="근무 시간을 기록합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="outline" onClick={onClose}>임시 저장</Button>
          <Button onClick={onClose}>제출</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <FormField label="타임시트 코드" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="시작일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="종료일" required>
            <FormInput type="date" />
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">근무 기록</h3>
            <Button variant="outline" size="sm" onClick={addEntry}>
              <Plus className="w-4 h-4 mr-1" />기록 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium w-28">날짜</th>
                  <th className="text-left p-3 font-medium">프로젝트</th>
                  <th className="text-left p-3 font-medium">작업</th>
                  <th className="text-right p-3 font-medium w-20">시간</th>
                  <th className="text-left p-3 font-medium">설명</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormInput type="date" />
                    </td>
                    <td className="p-2">
                      <FormSelect>
                        <option value="">프로젝트 선택</option>
                        <option value="p001">스마트팜 박스 개발</option>
                        <option value="p002">친환경 포장재 연구</option>
                        <option value="p003">물류 시스템 개선</option>
                      </FormSelect>
                    </td>
                    <td className="p-2">
                      <FormSelect>
                        <option value="">작업 선택</option>
                        <option value="t001">요구사항 분석</option>
                        <option value="t002">설계</option>
                        <option value="t003">개발</option>
                        <option value="t004">테스트</option>
                      </FormSelect>
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={0} step={0.5} defaultValue={0} className="text-right" />
                    </td>
                    <td className="p-2">
                      <FormInput placeholder="작업 내용" />
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => removeEntry(index)}
                        className="p-1 rounded hover:bg-accent"
                        disabled={entries.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30">
                <tr>
                  <td colSpan={3} className="p-3 font-medium text-right">합계</td>
                  <td className="p-3 text-right font-bold">0h</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <FormField label="메모">
          <FormTextarea rows={2} placeholder="추가 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function TimesheetListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [expandedTimesheet, setExpandedTimesheet] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return timesheets.filter((timesheet) => {
      const matchesSearch =
        timesheet.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        timesheet.employeeName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = selectedStatus === 'all' || timesheet.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, selectedStatus]);

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    timesheets.forEach((timesheet) => {
      stats[timesheet.status] = (stats[timesheet.status] || 0) + 1;
    });
    return stats;
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedTimesheet(expandedTimesheet === id ? null : id);
  };

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[color] || colors.gray;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'submitted':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="타임시트 관리"
        description="근무 시간을 기록하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            타임시트 작성
          </button>
        }
      />

      {/* 상태별 요약 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(timesheetStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{config.label}</div>
            <div className="text-xl font-bold mt-1">{statusStats[status] || 0}개</div>
          </button>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="타임시트 코드, 직원명 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(timesheetStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 타임시트 목록 */}
      <div className="space-y-4">
        {filteredData.map((timesheet) => {
          const isExpanded = expandedTimesheet === timesheet.id;
          const statusConfig = timesheetStatusConfig[timesheet.status];

          return (
            <div
              key={timesheet.id}
              className="rounded-lg border border-border bg-card overflow-hidden"
            >
              {/* 타임시트 헤더 */}
              <div
                className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => toggleExpand(timesheet.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-primary">{timesheet.code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{timesheet.employeeName}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">총 근무시간</div>
                      <div className="text-xl font-bold">{timesheet.totalHours}h</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">기간</div>
                        <div className="text-sm">{timesheet.startDate} ~ {timesheet.endDate}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="p-1.5 rounded hover:bg-accent"
                          title="보기"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Eye className="w-4 h-4 text-muted-foreground" />
                        </button>
                        <button
                          className="p-1.5 rounded hover:bg-accent"
                          title="수정"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Edit2 className="w-4 h-4 text-muted-foreground" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 타임시트 상세 */}
              {isExpanded && (
                <div className="border-t border-border p-4 bg-accent/30">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    근무 기록 ({timesheet.entries.length}개)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 font-medium">날짜</th>
                          <th className="text-left py-2 px-3 font-medium">프로젝트</th>
                          <th className="text-left py-2 px-3 font-medium">작업</th>
                          <th className="text-right py-2 px-3 font-medium">시간</th>
                          <th className="text-left py-2 px-3 font-medium">설명</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timesheet.entries.map((entry) => (
                          <tr key={entry.id} className="border-b border-border last:border-0">
                            <td className="py-2 px-3">{entry.date}</td>
                            <td className="py-2 px-3">{entry.projectName}</td>
                            <td className="py-2 px-3">{entry.taskName}</td>
                            <td className="py-2 px-3 text-right font-medium">{entry.hours}h</td>
                            <td className="py-2 px-3 text-muted-foreground">{entry.description}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-primary/10">
                          <td colSpan={3} className="py-2 px-3 font-medium">합계</td>
                          <td className="py-2 px-3 text-right font-bold">{timesheet.totalHours}h</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* 승인 정보 */}
                  {(timesheet.status === 'submitted' || timesheet.status === 'approved') && (
                    <div className="mt-4 p-3 rounded-lg bg-background border border-border">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(timesheet.status)}
                        <span className="text-sm">
                          {timesheet.status === 'approved'
                            ? `${timesheet.approvedBy}님이 ${timesheet.approvedAt}에 승인함`
                            : '승인 대기 중'
                          }
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredData.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          검색 결과가 없습니다.
        </div>
      )}

      {/* 타임시트 작성 모달 */}
      <TimesheetAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
